import { memo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Table, Tbody, Td, Th, Thead, Tr } from '../../atoms'

interface Column {
  key: string
  label: string
}

interface DataTableProps {
  columns: Column[]
  rows: Record<string, ReactNode>[]
  isLoading?: boolean
  // Replaces the default "No data" text, e.g. when a search matched nothing
  emptyMessage?: ReactNode
  // Shown under the empty message, e.g. a button that creates the first record
  emptyAction?: ReactNode
  // The list query failed: show that instead of an empty table, with Retry when given
  error?: unknown
  onRetry?: () => void
}

// Lucide "circle-alert"
const ErrorIcon = () => (
  <svg
    className="data-table__error-icon"
    viewBox="0 0 24 24"
    width="40"
    height="40"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" x2="12" y1="8" y2="12" />
    <line x1="12" x2="12.01" y1="16" y2="16" />
  </svg>
)

// Lucide "folder-open"
const EmptyIcon = () => (
  <svg
    className="data-table__empty-icon"
    viewBox="0 0 24 24"
    width="40"
    height="40"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <path d="m6 14 1.5-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2" />
  </svg>
)

export const DataTable = memo(
  ({ columns, rows, isLoading, emptyMessage, emptyAction, error, onRetry }: DataTableProps) => {
    const { t } = useTranslation('admin-mobifull')

    return (
      <Table className="data-table">
        <Thead>
          <Tr>
            {columns.map(col => (
              <Th key={col.key}>{col.label}</Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {isLoading ? (
            <Tr>
              <Td colSpan={columns.length} className="data-table__loading">
                <div className="data-table__spinner-container">
                  <span className="data-table__spinner" aria-hidden="true" />
                  <span>{t('common.loading')}</span>
                </div>
              </Td>
            </Tr>
          ) : error && rows.length === 0 ? (
            <Tr>
              <Td colSpan={columns.length} className="data-table__empty">
                <div className="data-table__empty-container" role="alert">
                  <ErrorIcon />
                  <span>{t('common.loadError')}</span>
                  {onRetry ? (
                    <Button variant="secondary" onClick={() => onRetry()}>
                      {t('common.actions.retry')}
                    </Button>
                  ) : null}
                </div>
              </Td>
            </Tr>
          ) : rows.length === 0 ? (
            <Tr>
              <Td colSpan={columns.length} className="data-table__empty">
                <div className="data-table__empty-container">
                  <EmptyIcon />
                  <span>{emptyMessage ?? t('common.noData')}</span>
                  {emptyAction}
                </div>
              </Td>
            </Tr>
          ) : (
            rows.map((row, i) => (
              <Tr key={typeof row.id === 'string' || typeof row.id === 'number' ? row.id : i}>
                {columns.map(col => (
                  <Td key={col.key}>{row[col.key]}</Td>
                ))}
              </Tr>
            ))
          )}
        </Tbody>
      </Table>
    )
  },
)

DataTable.displayName = 'DataTable'
